// What the visitor's own browser remembers: where they stopped, what they closed. Nothing here is sent anywhere.
// Storage can be missing or full (private windows), so every call is allowed to fail quietly.
const PREFIX = 'vs:';

/** A short fingerprint of a piece of text, so something the visitor closed comes back when its wording changes. */
export const hash = (s) => [...String(s)].reduce((a, c) => (a * 31 + c.charCodeAt(0)) | 0, 7).toString(36);

const kindOf = (v) => (v === null ? 'null' : Array.isArray(v) ? 'array' : typeof v);
const isObject = (v) => kindOf(v) === 'object';

/**
 * A stored value, or the fallback when there is none, it cannot be read, or it is not the kind of thing the caller expects. Another
 * version of the site, another script, or a damaged value can sit under the same name; nothing here trusts it. (A fallback of null
 * means "anything": the caller checks.)
 */
export function load(key, fallback) {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (raw === null) return fallback;
    const value = JSON.parse(raw);
    return fallback === null || kindOf(value) === kindOf(fallback) ? value : fallback;
  } catch {
    return fallback;
  }
}
export function save(key, value) {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    // ignore
  }
}

/** What lasts only as long as this visit (a tab's session): it is gone when the tab is closed. */
export const session = {
  get(key, fallback) {
    try {
      const raw = sessionStorage.getItem(PREFIX + key);
      if (raw === null) return fallback;
      const value = JSON.parse(raw);
      return fallback === null || kindOf(value) === kindOf(fallback) ? value : fallback;
    } catch {
      return fallback;
    }
  },
  set(key, value) {
    try {
      sessionStorage.setItem(PREFIX + key, JSON.stringify(value));
    } catch {
      // ignore
    }
  },
};

/** One entry of "where each drama was left", or null when it is not one (a damaged or foreign value). */
function cleanProgress(v) {
  if (!isObject(v)) return null;
  const episode = Math.trunc(Number(v.episode));
  if (!(episode >= 1)) return null;
  return {
    episode,
    time: Math.max(0, Number(v.time) || 0),
    at: Number(v.at) || 0,
    title: typeof v.title === 'string' ? v.title : '',
    cover: typeof v.cover === 'string' ? v.cover : null,
    episodes: Math.max(0, Math.trunc(Number(v.episodes)) || 0),
  };
}
const readProgress = () => {
  const out = {};
  for (const [id, v] of Object.entries(load('progress', {}))) {
    const entry = cleanProgress(v);
    if (entry) out[id] = entry;
  }
  return out;
};

/** Where each drama was left: { [dramaId]: { episode, time, at, title, cover, episodes } } */
export const progress = {
  all: readProgress,
  get: (id) => readProgress()[id] ?? null,
  set(id, entry) {
    const all = readProgress();
    all[id] = { ...all[id], ...entry, at: Date.now() };
    // Keep the 60 most recent, so this cannot grow without end.
    const keep = Object.entries(all).sort((a, b) => b[1].at - a[1].at).slice(0, 60);
    save('progress', Object.fromEntries(keep));
  },
  recent: (n = 12) => Object.entries(readProgress()).sort((a, b) => b[1].at - a[1].at).slice(0, n).map(([id, v]) => ({ id, ...v })),
};

/** The saved dramas that are really saved dramas: an id and a title, whatever else sits in the list is left out. */
const readFavorites = () =>
  load('favorites', [])
    .filter((f) => isObject(f) && typeof f.id === 'string' && f.id)
    .map((f) => ({
      id: f.id,
      title: typeof f.title === 'string' ? f.title : '',
      cover: typeof f.cover === 'string' ? f.cover : null,
      episodes: Math.max(0, Math.trunc(Number(f.episodes)) || 0),
      rating: typeof f.rating === 'number' && Number.isFinite(f.rating) ? f.rating : null,
      h265Episodes: Math.max(0, Math.trunc(Number(f.h265Episodes)) || 0),
    }));

/** The dramas the visitor saved to "My list", newest first. Kept in this browser only. */
export const favorites = {
  all: readFavorites,
  has: (id) => readFavorites().some((f) => f.id === id),
  /** Saves or removes a drama. Returns whether it is now saved. */
  toggle(d) {
    const list = readFavorites();
    const at = list.findIndex((f) => f.id === d.id);
    if (at >= 0) list.splice(at, 1);
    else list.unshift({ id: d.id, title: d.title, cover: d.cover ?? null, episodes: d.episodes ?? 0, rating: d.rating ?? null, h265Episodes: 0 });
    save('favorites', list.slice(0, 200));
    return at < 0;
  },
};

/** Episodes the visitor has finished, so the list can mark them. */
export const seen = {
  get: (id) => new Set(load('seen:' + id, []).filter(Number.isInteger)),
  add(id, episode) {
    const set = new Set(load('seen:' + id, []).filter(Number.isInteger));
    set.add(episode);
    save('seen:' + id, [...set].slice(-400));
  },
};

/** The token that switches the ads off in this browser while a subscription lasts, and the code to restore it elsewhere. */
export const access = {
  token: () => (typeof load('access', null) === 'string' ? load('access', null) : null),
  set: (token) => save('access', token),
  code: () => (typeof load('code', null) === 'string' ? load('code', null) : null),
  setCode: (code) => save('code', code),
  clear() {
    save('access', null);
    save('code', null);
  },
};
